import React, { useState } from 'react';

const fmt = (v) => {
  const n = parseFloat(v);
  if (!n || isNaN(n)) return '--';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
};

const fmtUnit = (v) => {
  const n = parseFloat(v);
  if (!n || isNaN(n)) return '--';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: 'CAD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
};

const fmtPct = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '--';
  return `${n.toFixed(1)}%`;
};

/**
 * FinancialTab - Wired to Phase 1 + 2 + 2b + 2c data
 * Uses cross-validated pricing from the post-merge price validator when available.
 */
const FinancialTab = ({ tender, phase1Data, phase2Data, phase2bData, phase2cData }) => {
  const [selectedScenario, setSelectedScenario] = useState('balanced');

  const products = phase1Data?.products || [];
  const webPricing = phase2bData?.product_pricing || [];
  const shoppingListings = phase2bData?.shopping_listings || {};
  const strategies = phase2cData?.product_strategies || [];
  const pricingComparisons = phase2Data?.pricing_comparisons || [];
  const historicals = phase2Data?.historical_contracts || [];
  const markupPct = phase2Data?.recommended_markup_pct ?? 25;
  const wholesaleTotal = parseFloat(phase1Data?.total_wholesale_estimate) || 0;

  // Build lookup maps for cross-referencing by product name
  const webPriceMap = {};
  webPricing.forEach(wp => {
    const name = (wp.product_name || '').toLowerCase();
    if (!webPriceMap[name] || (wp.listed_price && !webPriceMap[name].listed_price)) {
      webPriceMap[name] = wp;
    }
  });

  const strategyMap = {};
  strategies.forEach(s => {
    strategyMap[(s.product_name || '').toLowerCase()] = s;
  });

  // Build lookup from validated Phase 2 pricing comparisons
  const validatedMap = {};
  pricingComparisons.forEach(pc => {
    validatedMap[(pc.product_name || '').toLowerCase()] = pc;
  });

  // Compute total extended cost (acquisition baseline) — needed before scenario calculations
  let totalExtCost = 0;
  products.forEach(p => {
    const key = (p.product_name || '').toLowerCase();
    const v = validatedMap[key];
    const bestCost = parseFloat(v?.best_cost_cad) || parseFloat(p.wholesale_price_cad) || 0;
    const qty = parseFloat(p.quantity) || 0;
    totalExtCost += qty * bestCost;
  });

  // Award band from historicals (informational context for Award Band section)
  const historicalValues = historicals
    .map(h => parseFloat(h.value))
    .filter(v => v && !isNaN(v));
  const awardMin = historicalValues.length ? Math.min(...historicalValues) : null;
  const awardMax = historicalValues.length ? Math.max(...historicalValues) : null;
  const awardAvg = historicalValues.length
    ? historicalValues.reduce((a, b) => a + b, 0) / historicalValues.length
    : null;

  // Pricing scenarios — anchored to actual product costs with AI-recommended markup.
  // Historical awards are NOT used as price anchors (they represent different contract scopes).
  const costBase = totalExtCost > 0 ? totalExtCost : wholesaleTotal;
  const scenario1 = costBase > 0 ? costBase * (1 + (markupPct * 0.6) / 100) : 0;  // Win: conservative markup
  const scenario2 = costBase > 0 ? costBase * (1 + markupPct / 100) : 0;          // Balanced: recommended markup
  const scenario3 = costBase > 0 ? costBase * (1 + (markupPct * 1.5) / 100) : 0;  // Max: aggressive markup

  const margin1 = costBase > 0 ? ((scenario1 - costBase) / costBase * 100) : 0;
  const margin2 = costBase > 0 ? ((scenario2 - costBase) / costBase * 100) : 0;
  const margin3 = costBase > 0 ? ((scenario3 - costBase) / costBase * 100) : 0;

  // Scenario map for bid pricing sheet
  const scenarioMap = {
    win: { label: 'Win-Focused', total: scenario1, margin: margin1, color: '#1e40af' },
    balanced: { label: 'Balanced', total: scenario2, margin: margin2, color: '#166534' },
    max: { label: 'Maximum Profit', total: scenario3, margin: margin3, color: '#92400e' },
  };
  const active = scenarioMap[selectedScenario] || scenarioMap.balanced;
  const ratio = (costBase > 0 && active.total > 0) ? active.total / costBase : 1;

  const hasNoData = !phase1Data && !phase2Data && !phase2bData && !phase2cData;

  if (hasNoData) {
    return (
      <div className="financial-tab">
        <div className="no-assessment-card">
          <div className="no-assessment-icon">💰</div>
          <h4>No Financial Data Yet</h4>
          <p>Run the analysis to generate financial insights from Phase 1, 2, 2b, and 2c data.</p>
        </div>
      </div>
    );
  }

  // Rationale text for each scenario
  const rationales = {
    win: `Conservative ${fmtPct(margin1)} markup over ${fmt(costBase)} acquisition cost. Lean pricing to maximize win probability.`,
    balanced: `Recommended ${fmtPct(margin2)} markup over ${fmt(costBase)} acquisition cost. Competitive midpoint balancing margin and win rate.`,
    max: `Aggressive ${fmtPct(margin3)} markup for maximum margin. Best when competition is limited or evaluation is technically weighted.`,
  };

  // Build bid pricing sheet line items
  const bidLineItems = products.map((p, i) => {
    const key = (p.product_name || '').toLowerCase();
    const v = validatedMap[key];
    const unitCost = parseFloat(v?.best_cost_cad) || parseFloat(p.wholesale_price_cad) || 0;
    const qty = parseFloat(p.quantity) || 0;
    const unitBidPrice = unitCost * ratio;
    const extended = qty * unitBidPrice;
    return {
      index: i + 1,
      name: p.product_name,
      qty,
      unit: p.unit || 'EA',
      unitCost,
      markup: ratio > 0 ? (ratio - 1) * 100 : 0,
      unitBidPrice,
      extended,
    };
  });
  const bidTotal = bidLineItems.reduce((sum, li) => sum + li.extended, 0);

  // Check if historical awards are comparable to this tender's scope.
  // If our bid is <20% of the historical average, the contracts are for different scopes.
  const historicalsComparable = awardAvg && costBase > 0 && (costBase / awardAvg) > 0.2;

  // Award band scenario position helper
  const bandPosition = (val) => {
    if (awardMin === null || awardMax === null || awardMax === awardMin) return 50;
    const pct = ((val - awardMin) / (awardMax - awardMin)) * 100;
    return Math.max(2, Math.min(98, pct));
  };

  return (
    <div className="financial-tab">
      {/* 1a. Cost Breakdown Table */}
      {products.length > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Cost Breakdown</h4>
          <table className="products-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Wholesale</th>
                <th>Retail</th>
                <th>Web Price</th>
                <th>Factory Landed</th>
                <th>Best Cost</th>
                <th>Ext. Cost</th>
                <th>Route</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => {
                const key = (p.product_name || '').toLowerCase();
                const wp = webPriceMap[key];
                const st = strategyMap[key];
                const v = validatedMap[key];
                // Use validated factory landed if available, fall back to raw Phase 2c
                const factoryLanded = v?.factory_landed_cad || st?.overseas?.landed_cost_cad;
                const bestCost = v?.best_cost_cad;
                const route = v?.sourcing_route || (st?.recommendation === 'OVERSEAS' ? 'factory' : st?.recommendation === 'DOMESTIC' ? 'domestic' : null);
                const routeStyles = {
                  domestic: { background: '#dbeafe', color: '#1d4ed8', label: 'Domestic' },
                  factory: { background: '#fce7f3', color: '#be185d', label: 'Factory' },
                };
                const badge = route ? (routeStyles[route] || { background: '#f3f4f6', color: '#6b7280', label: route }) : null;
                const qty = parseFloat(p.quantity) || 0;
                const bestCostVal = parseFloat(bestCost) || parseFloat(p.wholesale_price_cad) || 0;
                const extCost = qty * bestCostVal;
                return (
                  <tr key={i}>
                    <td>
                      {p.product_name}
                      {v?.sourcing_note && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{v.sourcing_note}</div>
                      )}
                    </td>
                    <td>{p.quantity} {p.unit}</td>
                    <td className="currency">{fmt(p.wholesale_price_cad)}</td>
                    <td className="currency">{fmt(p.retail_price_cad)}</td>
                    <td className="currency">
                      {(() => {
                        // Prefer Google Shopping API data (structured, verified)
                        const shopItems = shoppingListings[p.product_name] || shoppingListings[(p.product_name || '').toLowerCase()] || [];
                        const bestShop = shopItems.find(s => s.price_cad) || null;
                        if (bestShop) {
                          return (
                            <>
                              {fmt(bestShop.price_cad)}
                              <span style={{ fontSize: 10, color: '#6b7280', display: 'block' }}>
                                {bestShop.merchant}
                              </span>
                              {shopItems.length > 1 && (
                                <span style={{ fontSize: 9, color: '#9ca3af', display: 'block' }}>
                                  +{shopItems.length - 1} more
                                </span>
                              )}
                            </>
                          );
                        }
                        // Fall back to Claude web pricing
                        return (
                          <>
                            {wp?.listed_price ? fmt(wp.listed_price) : '--'}
                            {wp?.source_name && <span style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>{wp.source_name}</span>}
                          </>
                        );
                      })()}
                    </td>
                    <td className="currency">
                      {factoryLanded ? fmt(factoryLanded) : '--'}
                      {v?.price_sources?.some(s => s.includes('Alibaba')) && (
                        <span style={{
                          display: 'inline-block', marginLeft: 4, fontSize: 9, padding: '1px 4px',
                          borderRadius: 3, fontWeight: 600,
                          background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                        }}>
                          Alibaba
                        </span>
                      )}
                    </td>
                    <td className="currency" style={{ fontWeight: 600, background: bestCost ? '#f0fdf4' : undefined }}>
                      {bestCost ? fmt(bestCost) : '--'}
                    </td>
                    <td className="currency" style={{ fontWeight: 600 }}>
                      {extCost > 0 ? fmt(extCost) : '--'}
                    </td>
                    <td>
                      {badge && (
                        <span
                          title={v?.sourcing_note || ''}
                          style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                            fontSize: 11, fontWeight: 600, background: badge.background, color: badge.color,
                          }}
                        >
                          {badge.label}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}><strong>Totals</strong></td>
                <td className="currency"><strong>{fmt(phase1Data?.total_wholesale_estimate)}</strong></td>
                <td className="currency"><strong>{fmt(phase1Data?.total_retail_estimate)}</strong></td>
                <td colSpan={3}></td>
                <td className="currency"><strong>{totalExtCost > 0 ? fmt(totalExtCost) : '--'}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* 1b. Pricing Scenarios */}
      {costBase > 0 && (
        <div className="summary-section">
          <h4 className="summary-section-title">Pricing Scenarios</h4>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
            {`Based on ${fmtPct(markupPct)} recommended markup over ${fmt(costBase)} acquisition cost`}
          </p>
          <div className="preview-scenarios">
            <div
              className={`preview-scenario-card high ${selectedScenario === 'win' ? 'scenario-card-selected' : ''}`}
              onClick={() => setSelectedScenario('win')}
              style={{ cursor: 'pointer' }}
            >
              <div className="scenario-label">Win-Focused</div>
              <div className="scenario-value" style={{ color: '#1e40af' }}>{fmt(scenario1)}</div>
              <div className="scenario-detail-label">Total Bid</div>
              <div className="scenario-detail">
                Profit: {fmt(scenario1 - costBase)} &middot; {fmtPct(margin1)} margin
              </div>
              <div className="scenario-bar">
                <div className="scenario-bar-fill" style={{ width: '80%', background: '#3b82f6' }}></div>
              </div>
              <div className="scenario-rationale">Lean pricing to maximize win rate</div>
            </div>
            <div
              className={`preview-scenario-card mid ${selectedScenario === 'balanced' ? 'scenario-card-selected' : ''}`}
              onClick={() => setSelectedScenario('balanced')}
              style={{ cursor: 'pointer' }}
            >
              <div className="scenario-label">Balanced</div>
              <div className="scenario-value" style={{ color: '#166534' }}>{fmt(scenario2)}</div>
              <div className="scenario-detail-label">Total Bid</div>
              <div className="scenario-detail">
                Profit: {fmt(scenario2 - costBase)} &middot; {fmtPct(margin2)} margin
              </div>
              <div className="scenario-bar">
                <div className="scenario-bar-fill" style={{ width: '55%', background: '#10b981' }}></div>
              </div>
              <div className="scenario-rationale">AI-recommended competitive markup</div>
            </div>
            <div
              className={`preview-scenario-card low ${selectedScenario === 'max' ? 'scenario-card-selected' : ''}`}
              onClick={() => setSelectedScenario('max')}
              style={{ cursor: 'pointer' }}
            >
              <div className="scenario-label">Maximum Profit</div>
              <div className="scenario-value" style={{ color: '#92400e' }}>{fmt(scenario3)}</div>
              <div className="scenario-detail-label">Total Bid</div>
              <div className="scenario-detail">
                Profit: {fmt(scenario3 - costBase)} &middot; {fmtPct(margin3)} margin
              </div>
              <div className="scenario-bar">
                <div className="scenario-bar-fill" style={{ width: '30%', background: '#f59e0b' }}></div>
              </div>
              <div className="scenario-rationale">Premium pricing for maximum margin</div>
            </div>
          </div>

          {/* Bid Pricing Sheet for selected scenario */}
          {products.length > 0 && costBase > 0 && (
            <div style={{ marginTop: 20 }}>
              <h4 className="summary-section-title" style={{ fontSize: 13 }}>
                Bid Pricing Sheet — {active.label}
              </h4>
              <table className="bid-pricing-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Unit Cost</th>
                    <th>Markup</th>
                    <th>Unit Bid Price</th>
                    <th>Extended</th>
                  </tr>
                </thead>
                <tbody>
                  {bidLineItems.map(li => (
                    <tr key={li.index}>
                      <td>{li.index}</td>
                      <td>{li.name}</td>
                      <td>{li.qty}</td>
                      <td>{li.unit}</td>
                      <td className="currency">{fmtUnit(li.unitCost)}</td>
                      <td>{fmtPct(li.markup)}</td>
                      <td className="currency" style={{ fontWeight: 600, color: active.color }}>{fmtUnit(li.unitBidPrice)}</td>
                      <td className="currency" style={{ fontWeight: 600 }}>{fmt(li.extended)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}></td>
                    <td><strong>TOTAL</strong></td>
                    <td></td>
                    <td className="currency"><strong style={{ color: active.color }}>{fmt(bidTotal)}</strong></td>
                  </tr>
                </tfoot>
              </table>

              {/* Rationale */}
              <div className="scenario-rationale-block">
                <p>{rationales[selectedScenario]}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 1c. Likely Award Band */}
      {awardMin !== null && (
        <div className="summary-section">
          <h4 className="summary-section-title">Likely Award Band</h4>
          <div className="preview-award-band">
            <div className="award-band-visual">
              {historicalValues.length === 1 ? (
                /* Single contract — show single value, not a misleading range */
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#6366f1' }}>{fmt(awardAvg)}</div>
                  <div className="band-label" style={{ marginTop: 6 }}>
                    Based on 1 historical contract
                  </div>
                </div>
              ) : (
                /* Multiple contracts — show range */
                <>
                  <div className="band-range">
                    <span className="band-low" style={{ color: '#374151' }}>{fmt(awardMin)}</span>
                    <div className="band-bar" style={{ background: 'linear-gradient(90deg, #dbeafe, #93c5fd, #dbeafe)' }}>
                      <div className="band-fill" style={{ background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', opacity: 0.6 }}></div>
                      <div className="band-marker" style={{ background: '#6366f1' }}></div>
                      {/* Only show scenario markers when historicals are comparable scope */}
                      {historicalsComparable && (
                        <>
                          <div
                            className="band-scenario-marker"
                            style={{ left: `${bandPosition(scenario1)}%`, background: '#3b82f6' }}
                            title={`Win-Focused: ${fmt(scenario1)}`}
                          />
                          <div
                            className="band-scenario-marker"
                            style={{ left: `${bandPosition(scenario2)}%`, background: '#10b981' }}
                            title={`Balanced: ${fmt(scenario2)}`}
                          />
                          <div
                            className="band-scenario-marker"
                            style={{ left: `${bandPosition(scenario3)}%`, background: '#f59e0b' }}
                            title={`Max Profit: ${fmt(scenario3)}`}
                          />
                        </>
                      )}
                    </div>
                    <span className="band-high" style={{ color: '#374151' }}>{fmt(awardMax)}</span>
                  </div>
                  {historicalsComparable && (
                    <div className="band-scenario-legend">
                      <span className="band-legend-item">
                        <span className="band-legend-dot" style={{ background: '#3b82f6' }} /> Win {fmt(scenario1)}
                      </span>
                      <span className="band-legend-item">
                        <span className="band-legend-dot" style={{ background: '#10b981' }} /> Balanced {fmt(scenario2)}
                      </span>
                      <span className="band-legend-item">
                        <span className="band-legend-dot" style={{ background: '#f59e0b' }} /> Max {fmt(scenario3)}
                      </span>
                    </div>
                  )}
                  <div style={{ textAlign: 'center', fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    Average: {fmt(awardAvg)}
                  </div>
                  {!historicalsComparable && (
                    <div style={{ textAlign: 'center', fontSize: 12, color: '#b45309', marginTop: 4, fontStyle: 'italic' }}>
                      Note: Historical contracts are for different scopes/quantities and not directly comparable to this tender.
                    </div>
                  )}
                  {historicalsComparable && awardMax > awardMin && (
                    <div style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      Your <strong>{active.label}</strong> bid of {fmt(active.total)} sits at{' '}
                      <strong>{Math.round(bandPosition(active.total))}%</strong> of the historical range
                    </div>
                  )}
                  <div className="band-label">
                    Based on {historicalValues.length} historical contracts
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 1d. Sourcing Strategy Summary */}
      {phase2cData?.narrative && (
        <div className="summary-section">
          <h4 className="summary-section-title">Sourcing Strategy</h4>
          {phase2cData.sourcing_recommendation && (
            <div style={{ marginBottom: 12 }}>
              <span className={`qual-chip ${
                phase2cData.sourcing_recommendation === 'DOMESTIC' ? 'green' :
                phase2cData.sourcing_recommendation === 'OVERSEAS' ? 'blue' : 'amber'
              }`}>
                {phase2cData.sourcing_recommendation}
              </span>
            </div>
          )}
          <div className="ai-summary-card">
            <p className="ai-summary-text">{phase2cData.narrative}</p>
          </div>

          {/* Per-product sourcing decisions table */}
          {strategies.length > 0 && (
            <table className="sourcing-decisions-table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Route</th>
                  <th>Savings vs Alt.</th>
                  <th>Rationale</th>
                </tr>
              </thead>
              <tbody>
                {strategies.map((s, i) => {
                  const rec = s.recommendation || '--';
                  const savings = s.savings_vs_alternative_pct != null
                    ? fmtPct(s.savings_vs_alternative_pct)
                    : (s.domestic?.unit_price_cad && s.overseas?.landed_cost_cad
                        ? fmtPct(Math.abs(
                            ((parseFloat(s.domestic.unit_price_cad) - parseFloat(s.overseas.landed_cost_cad))
                              / parseFloat(s.domestic.unit_price_cad)) * 100
                          ))
                        : '--');
                  return (
                    <tr key={i}>
                      <td>{s.product_name}</td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                            fontSize: 11, fontWeight: 600,
                            background: rec === 'OVERSEAS' ? '#fce7f3' : rec === 'DOMESTIC' ? '#dbeafe' : '#f3f4f6',
                            color: rec === 'OVERSEAS' ? '#be185d' : rec === 'DOMESTIC' ? '#1d4ed8' : '#6b7280',
                          }}
                        >
                          {rec}
                        </span>
                      </td>
                      <td>{savings}</td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{s.rationale || s.reasoning || '--'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Phase 2 Arbitrage Heuristic */}
      {phase2Data?.arbitrage_heuristic && (
        <div className="summary-section">
          <h4 className="summary-section-title">Arbitrage Insight</h4>

          {/* Quick-numbers summary bar */}
          {costBase > 0 && (
            <div className="numbers-summary-bar">
              <div className="numbers-bar-item">
                <span className="numbers-bar-label">Total Acquisition Cost</span>
                <span className="numbers-bar-value">{fmt(totalExtCost || costBase)}</span>
              </div>
              <div className="numbers-bar-divider" />
              <div className="numbers-bar-item">
                <span className="numbers-bar-label">Recommended Bid</span>
                <span className="numbers-bar-value" style={{ color: active.color }}>{fmt(active.total)}</span>
              </div>
              <div className="numbers-bar-divider" />
              <div className="numbers-bar-item">
                <span className="numbers-bar-label">Expected Margin</span>
                <span className="numbers-bar-value">{fmtPct(active.margin)}</span>
              </div>
            </div>
          )}

          <div className="ai-summary-card">
            <p className="ai-summary-text">{phase2Data.arbitrage_heuristic}</p>
            {phase2Data.bid_recommendation && (
              <div className="ai-summary-meta">
                <span className="meta-item">
                  <strong>Recommendation:</strong> {phase2Data.bid_recommendation}
                </span>
                {phase2Data.recommended_markup_pct && (
                  <span className="meta-item">
                    <strong>Suggested Markup:</strong> {fmtPct(phase2Data.recommended_markup_pct)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialTab;
